// src/pages/Meters.js
import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import styles from '../Styles/Meters.module.css';
import IconMeter from '../Images/IconMeter';
import IconButton from '../Images/IconButton';
import { useToast } from '../Commoncontext/ToastContext';

/* ---------- Helpers ---------- */
const fmtDate = (ts) => {
  if (!ts) return '-';
  if (ts?._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('fi-FI');
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('fi-FI');
};
const parseISO = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};
const daysBetween = (a, b) => Math.round((b - a) / 86400000);

const TYPE_LABEL = { water: 'Vesi', electricity: 'Sähkö', heat: 'Lämpö' };
const UNIT_LABEL = { m3: 'm3', kwh: 'kWh' };
const METHOD_LABEL = { manual: 'Manuaalinen', integration: 'Integraatio' };

/* ---------- Kevyet inline-SVG ikonit ---------- */
const WaterIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M12 2s6 7.1 6 11.3A6 6 0 1 1 6 13.3C6 9.1 12 2 12 2z"/>
  </svg>
);
const PlugIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M7 2v5h2V2h2v5h2V2h2v6a5 5 0 0 1-4 4.9V22h-2v-9.1A5 5 0 0 1 5 8V2h2z"/>
  </svg>
);
const RadiatorIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M4 6h2v12H4V6zm4 0h2v12H8V6zm4 0h2v12h-2V6zm4 0h2v12h-2V6zM3 18h18v2H3v-2z"/>
  </svg>
);

/* ---------- Skeletoneille data ---------- */
const MOCK_METERS = [
  { id: 'm1', name: 'Vesimittari huoneisto A10', type: 'water', unit: 'm3',  method: 'integration', active: true,  lastReadingAt: '2025-09-01' },
  { id: 'm2', name: 'Sähkömittari autopaikka 8',       type: 'electricity', unit: 'kwh', method: 'manual',      active: true,  lastReadingAt: '2025-09-09' },
  { id: 'm3', name: 'Maalämpö',         type: 'heat',  unit: 'kwh', method: 'manual',      active: false, lastReadingAt: null },
];

/* ---------- Validointi ---------- */
const validateForm = (f) => {
  const e = {};
  if (!f.name?.trim()) e.name = 'Mittarin nimi on pakollinen.';
  if (!f.type) e.type = 'Valitse tyyppi.';
  if (!f.unit) e.unit = 'Valitse yksikkö.';
  if (!f.method) e.method = 'Valitse mittaustapa.';
  return e;
};

/* ---------- Deterministinen mock integraatiolle ---------- */
const hashStr = (str) => { let h=0; for (let i=0;i<str.length;i++){ h=(h<<5)-h+str.charCodeAt(i); h|=0; } return Math.abs(h); };
const seeded = (seed) => { let s=(seed%2147483647); if (s<=0) s+=2147483646; return () => (s=s*16807%2147483647)/2147483647; };

const daysFi = ['Su','Ma','Ti','Ke','To','Pe','La'];
const monthsFiShort = ['Tammi','Helmi','Maalis','Huhti','Touko','Kesä','Heinä','Elo','Syys','Loka','Marras','Joulu'];

function formatDayLabelSplit(d) {
  const top = daysFi[d.getDay()];
  const bottom = `${d.getDate()}.${d.getMonth()+1}`;
  return { top, bottom };
}
function getWeekNumber(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}

/* ---------- 20 bar (kylmä+lämmin) integraatiomittari ---------- */
function genLast20DaysColdHot(meterId, windowOffset = 0) {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - (19 + windowOffset * 20));
  const arr = [];
  for (let i = 0; i < 20; i++) {
    const d = new Date(start); d.setDate(start.getDate()+i);
    const seed = hashStr(`${meterId}|day|${d.toISOString().slice(0,10)}`);
    const rnd = seeded(seed);
    const cold = Math.round((0.2 + rnd()*2.8)*10)/10;
    const hot  = Math.round((0.1 + rnd()*1.0)*10)/10;
    const { top, bottom } = formatDayLabelSplit(d);
    arr.push({ key:d.toISOString().slice(0,10), labelTop: top, labelBottom: bottom, cold, hot, total:+(cold+hot).toFixed(1), date:d });
  }
  return arr;
}
function genLast20WeeksColdHot(meterId, windowOffset = 0) {
  const today = new Date();
  const current = new Date(today);
  const day = (current.getDay() || 7);
  current.setDate(current.getDate() - (day - 1));
  current.setDate(current.getDate() - (19 + windowOffset*20)*7);
  const arr = [];
  for (let i=0;i<20;i++){
    const monday = new Date(current); monday.setDate(current.getDate()+i*7);
    const seed = hashStr(`${meterId}|week|${monday.toISOString().slice(0,10)}`);
    const rnd = seeded(seed);
    const cold = Math.round((2.0 + rnd()*10.0)*10)/10;
    const hot  = Math.round((0.8 + rnd()*5.0)*10)/10;
    const w = getWeekNumber(monday);
    arr.push({ key:monday.toISOString().slice(0,10), labelTop:'Vko', labelBottom:String(w), cold, hot, total:+(cold+hot).toFixed(1), date:monday });
  }
  return arr;
}
function genLast20MonthsColdHot(meterId, windowOffset = 0) {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth() - (19 + windowOffset*20), 1);
  const arr = [];
  for (let i=0;i<20;i++){
    const d = new Date(start.getFullYear(), start.getMonth()+i, 1);
    const seed = hashStr(`${meterId}|month|${d.getFullYear()}-${d.getMonth()+1}`);
    const rnd = seeded(seed);
    const cold = Math.round((8 + rnd()*45)*10)/10;
    const hot  = Math.round((4 + rnd()*22)*10)/10;
    arr.push({ key:`${d.getFullYear()}-${d.getMonth()+1}`, labelTop: monthsFiShort[d.getMonth()], labelBottom: String(d.getFullYear()).slice(-2), cold, hot, total:+(cold+hot).toFixed(1), date:d });
  }
  return arr;
}

/* ---- KPI-apurit integraatiolle ---- */
function sumColdHot(rows) {
  return rows.reduce((a,d)=>({ cold:+(a.cold+d.cold).toFixed(1), hot:+(a.hot+d.hot).toFixed(1), total:+(a.total+d.total).toFixed(1)}), {cold:0,hot:0,total:0});
}
function genLastNDaysColdHot(meterId, n = 30) {
  const today=new Date(); const start=new Date(today); start.setDate(start.getDate()-(n-1)); const arr=[];
  for(let i=0;i<n;i++){ const d=new Date(start); d.setDate(start.getDate()+i); const seed=hashStr(`${meterId}|dayN|${d.toISOString().slice(0,10)}`); const rnd=seeded(seed);
    const cold=Math.round((0.2+rnd()*2.8)*10)/10; const hot=Math.round((0.1+rnd()*1.0)*10)/10; arr.push({ cold, hot, total:+(cold+hot).toFixed(1) }); }
  return arr;
}

/* Taloyhtiö (mock) */
function makeBuildingStats(meterId, windowSum, metersCount = 19) {
  const peers = metersCount - 1;
  const rnd = seeded(hashStr(`${meterId}|building-factor`));
  const factor = 6 + Math.floor(rnd() * 7);
  const building = {
    cold:  +(windowSum.cold  * factor).toFixed(1),
    hot:   +(windowSum.hot   * factor).toFixed(1),
    total: +(windowSum.total * factor).toFixed(1),
  };
  function distribute(key, residualTotal){
    const r=seeded(hashStr(`${meterId}|${key}-peers`));
    const weights=Array.from({length:peers},()=>0.2+r()); const wSum=weights.reduce((a,b)=>a+b,0);
    return weights.map(w=>+(residualTotal*(w/wSum)).toFixed(1));
  }
  const residualTotal=Math.max(0,building.total-windowSum.total);
  const residualCold=Math.max(0,building.cold-windowSum.cold);
  const residualHot=Math.max(0,building.hot-windowSum.hot);

  const peerTotals=distribute('total',residualTotal);
  const peerColds=distribute('cold',residualCold);
  const peerHots=distribute('hot',residualHot);

  const rankOf=(self, peersArr)=>[self,...peersArr].sort((a,b)=>b-a).findIndex(v=>v===self)+1;

  const ranks={ total:rankOf(windowSum.total,peerTotals), cold:rankOf(windowSum.cold,peerColds), hot:rankOf(windowSum.hot,peerHots) };
  const shares={ total: building.total>0 ? +(100*windowSum.total/building.total).toFixed(1):0,
                 cold:  building.cold>0  ? +(100*windowSum.cold /building.cold ).toFixed(1):0,
                 hot:   building.hot>0   ? +(100*windowSum.hot  /building.hot  ).toFixed(1):0 };
  return { building, shares, ranks, rankTotal: metersCount };
}

/* ---------- Perioditeksti integraatio ---------- */
function formatPeriodLabel(agg, data) {
  if (!data?.length) return '';
  const first = data[0], last = data[data.length-1];
  if (agg==='day'){
    const s=first.date, e=last.date;
    return `${daysFi[s.getDay()]} ${s.getDate()}.${s.getMonth()+1} – ${daysFi[e.getDay()]} ${e.getDate()}.${e.getMonth()+1}`;
  }
  if (agg==='week'){
    return `Vko ${first.labelBottom} – ${last.labelBottom}`;
  }
  const s=first.date, e=last.date;
  return `${monthsFiShort[s.getMonth()]} ${String(s.getFullYear()).slice(-2)} – ${monthsFiShort[e.getMonth()]} ${String(e.getFullYear()).slice(-2)}`;
}

/* ---------- SVG pino-barchart (integraatio) ---------- */
function BarChartStacked({ data, unit = 'm3', height = 340 }) {
  const max = Math.max(...data.map(d => d.total), 1);
  const padding = { top: 16, right: 16, bottom: 56, left: 44 };
  const width = Math.max(960, data.length * 52);
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const segment = innerW / data.length;
  const barW = segment * 0.62;
  const gap = segment - barW;
  const labelY1 = padding.top + innerH + 12;
  const labelY2 = padding.top + innerH + 26;

  return (
    <div className={styles.chartScroll}>
      <svg className={styles.chartSvg} width={width} height={height} role="img" aria-label={`Kulutus (${unit})`}>
        {[0,0.5,1].map((t,i)=>{
          const yVal=max*t; const y=padding.top+innerH-innerH*t;
          return (<g key={i}>
            <line x1={padding.left} y1={y} x2={width-padding.right} y2={y} className={styles.chartGrid}/>
            <text x={padding.left-8} y={y} className={styles.axisTick} textAnchor="end" dominantBaseline="middle">{yVal.toFixed(1)}</text>
          </g>);
        })}
        <text x={padding.left-28} y={padding.top+innerH/2} className={styles.axisLabel}
              transform={`rotate(-90, ${padding.left-28}, ${padding.top+innerH/2})`} textAnchor="middle">{unit}</text>

        {data.map((d,i)=>{
          const totalH=(d.total/max)*innerH, coldH=(d.cold/max)*innerH, hotH=(d.hot/max)*innerH;
          const x=padding.left+i*segment+gap/2;
          const yTop=padding.top+innerH-totalH;
          return (
            <g key={d.key||i}>
              <rect x={x} y={yTop} width={barW} height={(d.hot/max)*innerH} className={styles.barHot}/>
              <rect x={x} y={yTop+(hotH)} width={barW} height={coldH} className={styles.barCold}/>
              <text x={x+barW/2} y={yTop-6} className={styles.barValue} textAnchor="middle">{d.total.toFixed(1)}</text>
              <text x={x+barW/2} y={labelY1} className={styles.axisTick} textAnchor="middle">{d.labelTop}</text>
              <text x={x+barW/2} y={labelY2} className={styles.axisTick} textAnchor="middle">{d.labelBottom}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ======================================================================= */
/* ================== MANUAALISEN MITTARIN LOGIIKKA ====================== */
/* ======================================================================= */

function sortByDateAsc(rows){ return [...rows].sort((a,b)=> new Date(a.date) - new Date(b.date)); }
function computeDeltas(sortedArr){
  return sortedArr.map((r, idx)=>{
    if (idx===0) return { ...r, delta: null, days: null };
    const prev=sortedArr[idx-1];
    const deltaRaw = r.value - prev.value;
    const daysGap = daysBetween(parseISO(prev.date), parseISO(r.date));
    if (r.reset) return { ...r, delta: null, days: daysGap };
    const delta = deltaRaw < 0 ? null : +(deltaRaw).toFixed(1);
    return { ...r, delta, days: daysGap };
  });
}

/* Kulutus jaksolla: summataan ne deltat joiden molemmat päät osuvat ikkunaan */
function windowConsumption(withDeltas, startDate, endDate){
  if (!withDeltas.length) return { total: 0, count: 0, maxDelta: null };
  const s = startDate.getTime(), e = endDate.getTime();
  let total = 0; let count = 0; let maxDelta = null;
  for (let i=1;i<withDeltas.length;i++){
    const a = new Date(withDeltas[i-1].date).getTime();
    const b = new Date(withDeltas[i].date).getTime();
    const d = withDeltas[i];
    if (a >= s && b <= e && d.delta != null){
      total += d.delta;
      count++;
      if (!maxDelta || d.delta > maxDelta.value){ maxDelta = { value: d.delta, from: withDeltas[i-1].date, to: withDeltas[i].date }; }
    }
  }
  return { total: +total.toFixed(1), count, maxDelta };
}

function addDays(d, n){ const x=new Date(d); x.setDate(x.getDate()+n); return x; }

/* ======================================================================= */

export default function Meters() {
  const { showToast } = useToast();

  /* ---------- Suodatus ---------- */
  const [filter, setFilter] = useState('water');
  const [meters, setMeters] = useState(MOCK_METERS);

  const filtered = useMemo(() => {
    if (filter === 'all') return meters;
    return meters.filter(m => filter === 'water' ? m.type==='water'
                           : filter === 'electricity' ? m.type==='electricity'
                           : filter === 'heat' ? m.type==='heat' : true);
  }, [meters, filter]);

  /* ---------- Modaali (mittarin luonti/muokkaus) ---------- */
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [form, setForm] = useState({ id: undefined, name: '', type: '', unit: '', method: '', active: true, lastReadingAt: '' });
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef(null);

  /* ---------- Näkymät ---------- */
  const [view, setView] = useState('list');          // 'list' | 'readings' (integration) | 'manual' (manual)
  const [readingMeter, setReadingMeter] = useState(null); // { id, name, unit }
  const [agg, setAgg] = useState('day');             // integraatiolle
  const [cursor, setCursor] = useState(0);

  /* ---------- Manuaalilukemat (skeletontila) ---------- */
  const [manualDataByMeter, setManualDataByMeter] = useState({
    m2: [
      { id:'r1', date: '2025-03-01', value: 10850, note: '' },
      { id:'r2', date: '2025-06-15', value: 11610, note: '' },
      { id:'r3', date: '2025-08-26', value: 12310, note: '' },
      { id:'r4', date: '2025-08-29', value: 12380, note: '' },
      { id:'r5', date: '2025-09-02', value: 12435, note: 'remontti alkoi' },
      { id:'r6', date: '2025-09-05', value: 12490, note: '' },
      { id:'r7', date: '2025-09-09', value: 12560, note: '' },
    ],
  });

  /* ---------- Integraatiomittarin data ---------- */
  const readingsData = useMemo(() => {
    if (!readingMeter || view !== 'readings') return [];
    if (agg === 'day')  return genLast20DaysColdHot(readingMeter.id, cursor);
    if (agg === 'week') return genLast20WeeksColdHot(readingMeter.id, cursor);
    return genLast20MonthsColdHot(readingMeter.id, cursor);
  }, [readingMeter, agg, cursor, view]);

  const periodLabel = useMemo(() => view==='readings' ? formatPeriodLabel(agg, readingsData) : '', [agg, readingsData, view]);

  const windowSummary = useMemo(() => {
    if (!readingMeter || view!=='readings') return { cold:0, hot:0, total:0, title:'' };
    if (agg === 'day')  return { ...sumColdHot(genLastNDaysColdHot(readingMeter.id, 30)), title: 'Edelliset 30 päivää' };
    if (agg === 'week') return { ...sumColdHot(genLast20MonthsColdHot(readingMeter.id, 0).slice(-4)), title: 'Edelliset 4 kuukautta' };
    return { ...sumColdHot(genLast20MonthsColdHot(readingMeter.id, 0).slice(-12)), title: 'Edelliset 12 kuukautta' };
  }, [readingMeter, agg, view]);

  const buildingStats = useMemo(() => {
    if (!readingMeter || view!=='readings') return { building:{cold:0,hot:0,total:0}, shares:{cold:0,hot:0,total:0}, ranks:{cold:1,hot:1,total:1}, rankTotal:19 };
    return makeBuildingStats(readingMeter.id, windowSummary);
  }, [readingMeter, windowSummary, view]);

  /* ---------- Avaa riviltä ---------- */
  const openCreate = () => { setMode('create'); setForm({ id: undefined, name:'', type:'', unit:'', method:'', active:true, lastReadingAt:'' }); setTouched({}); setSubmitAttempted(false); setModalOpen(true); };
  const openEdit = (meter) => { setMode('edit'); setForm({ id:meter.id, name:meter.name||'', type:meter.type||'', unit:meter.unit||'', method:meter.method||'', active:!!meter.active, lastReadingAt:meter.lastReadingAt||''}); setTouched({}); setSubmitAttempted(false); setModalOpen(true); };
  const closeModal = useCallback(() => setModalOpen(false), []);
  useEffect(()=>{ if(!modalOpen) return; const onKeyDown=e=>{ if(e.key==='Escape') closeModal(); }; document.addEventListener('keydown', onKeyDown); return ()=>document.removeEventListener('keydown', onKeyDown);},[modalOpen, closeModal]);
  useEffect(()=>{ if(modalOpen) setTimeout(()=>nameRef.current?.focus(),0); },[modalOpen]);

  /* ---------- Toiminnot ---------- */
  const handleRowClick = (m) => openEdit(m);
  const handleEdit = (e, m) => { e.stopPropagation(); openEdit(m); };
  const handleOpenReadings = (e, m) => {
    e.stopPropagation();
    if (m.method === 'manual') {
      setReadingMeter({ id: m.id, name: m.name, unit: m.unit || 'kWh' });
      setView('manual');
    } else {
      setReadingMeter({ id: m.id, name: m.name, unit: m.unit || 'm3' });
      setAgg('day'); setCursor(0); setView('readings');
    }
  };
  const handleDelete = (e, m) => { e.stopPropagation(); setMeters(list => list.filter(x => x.id !== m.id)); showToast('Mittari poistettu (skeletontila).', 'success'); };

  const errors = useMemo(() => validateForm(form), [form]);
  const showErr = (key) => (errors[key] && (touched[key] || submitAttempted));
  const onChangeField = (k,v)=> setForm(p=>({...p,[k]:v}));
  const onBlurField = (k)=> setTouched(t=>({...t,[k]:true}));

  const onSubmit = async ()=> {
    setSubmitAttempted(true);
    if (Object.keys(errors).length>0) return;
    setBusy(true);
    try {
      if (mode==='create'){ const newOne={...form, id:`tmp_${Date.now()}`}; setMeters(list=>[newOne,...list]); showToast('Mittari luotu (skeletontila).','success'); }
      else { setMeters(list=>list.map(m=>(m.id===form.id?{...m,...form}:m))); showToast('Mittari päivitetty (skeletontila).','success'); }
      setModalOpen(false);
    } finally { setBusy(false); }
  };

  /* ===================== MANUAALINEN: UI & laskenta ===================== */

  // Jaksovalinta: 30 pv, 90 pv, 12 kk
  const [manPeriod, setManPeriod] = useState('30d'); // '30d' | '90d' | '12m'

  // Manuaalidata (nykyinen mittari)
  const manualRowsAsc = useMemo(()=>{
    if (!readingMeter || view!=='manual') return [];
    const rows = manualDataByMeter[readingMeter.id] || [];
    return sortByDateAsc(rows).map(r => ({ ...r, dateObj: parseISO(r.date) }));
  }, [manualDataByMeter, readingMeter, view]);

  const manualWithDeltas = useMemo(()=> computeDeltas(manualRowsAsc), [manualRowsAsc]);

  // Ikkunan alku/loppu: päätetään viimeisimmän lukeman mukaan
  const manWindow = useMemo(()=>{
    if (!manualWithDeltas.length) return null;
    const end = manualWithDeltas[manualWithDeltas.length-1].dateObj || new Date();
    const start = new Date(end);
    if (manPeriod === '30d') start.setDate(start.getDate() - 29);
    else if (manPeriod === '90d') start.setDate(start.getDate() - 89);
    else start.setMonth(start.getMonth() - 11);
    return { start, end };
  }, [manualWithDeltas, manPeriod]);

  // Kulutukset & johdannaiset
  const manSummary = useMemo(()=>{
    if (!manWindow) return { canCalc:false };
    const { start, end } = manWindow;
    const w = windowConsumption(manualWithDeltas, start, end); // summa & max delta
    const daysCount = Math.max(1, daysBetween(start, end) + 1);
    // Vertailujakso: sama pituus välittömästi ennen tarkastelujaksoa
    const prevStart = manPeriod==='12m' ? addDays(start, -365) : addDays(start, -(daysCount));
    const prevEnd   = addDays(start, -1);
    const prev      = windowConsumption(manualWithDeltas, prevStart, prevEnd);
    const trendPct  = prev.total > 0 ? +(((w.total - prev.total)/prev.total)*100).toFixed(1) : null;
    return {
      canCalc: w.count > 0,
      total: w.total,
      avgPerDay: +(w.total / daysCount).toFixed(2),
      maxDelta: w.maxDelta, // { value, from, to } | null
      trendPct,
      daysCount,
    };
  }, [manWindow, manualWithDeltas, manPeriod]);

  // Taulukon pikasuodattimet
  const [onlyResets, setOnlyResets] = useState(false);
  const [onlyAnomalies, setOnlyAnomalies] = useState(false);
  const [onlyDuplicates, setOnlyDuplicates] = useState(false);

  const manualDuplicates = useMemo(()=>{
    const map = new Map();
    manualRowsAsc.forEach(r => map.set(r.date, (map.get(r.date)||0) + 1));
    return new Set([...map.entries()].filter(([,c])=>c>1).map(([d])=>d));
  }, [manualRowsAsc]);

  const manualFilteredDesc = useMemo(()=>{
    let arr = [...manualWithDeltas].reverse(); // uusin ensin taulukkoon
    if (onlyResets) arr = arr.filter(r => r.reset);
    if (onlyAnomalies) arr = arr.filter(r => r.delta != null && r.delta < 0); // ei pitäisi olla, mutta näytetään jos on
    if (onlyDuplicates) arr = arr.filter(r => manualDuplicates.has(r.date));
    return arr;
  }, [manualWithDeltas, onlyResets, onlyAnomalies, onlyDuplicates, manualDuplicates]);

  /* ---------- Uuden lukeman dialogi ---------- */
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState(() => {
    const d=new Date(); const two=(n)=>String(n).padStart(2,'0');
    return `${d.getFullYear()}-${two(d.getMonth()+1)}-${two(d.getDate())}`;
  });
  const [addValue, setAddValue] = useState('');
  const [addNote, setAddNote] = useState('');
  const [addReset, setAddReset] = useState(false);
  const addDateRef = useRef(null);

  useEffect(()=>{ if(addOpen) setTimeout(()=>addDateRef.current?.focus(), 0); }, [addOpen]);

  const saveNewReading = () => {
    if (!readingMeter) return;
    const v = Number(addValue);
    if (!Number.isFinite(v)) { showToast('Syötä lukemaksi numero.', 'error'); return; }
    const exists = (manualDataByMeter[readingMeter.id] || []).some(r => r.date === addDate);
    let proceed = true;
    if (exists) proceed = window.confirm('Tälle päivälle on jo lukema. Korvataanko?');
    if (!proceed) return;

    const newRow = { id:`row_${Date.now()}`, date: addDate, value: v, note: addNote?.trim()||'', reset: addReset };
    setManualDataByMeter(map => {
      let arr = map[readingMeter.id] ? [...map[readingMeter.id]] : [];
      if (exists) arr = arr.filter(r => r.date !== addDate);
      arr.push(newRow);
      return { ...map, [readingMeter.id]: arr };
    });
    setAddOpen(false);
    setAddNote(''); setAddValue(''); setAddReset(false);
    showToast('Lukema tallennettu (skeletontila).', 'success');
  };

  /* ---------- Render ---------- */
  const renderTypeCell = (m) => (
    <div className={styles.typeCell}>
      {m.type === 'water' && <WaterIcon className={`${styles.typeIcon} ${styles.waterIcon}`} />}
      {m.type === 'electricity' && <PlugIcon className={`${styles.typeIcon} ${styles.electricIcon}`} />}
      {m.type === 'heat' && <RadiatorIcon className={`${styles.typeIcon} ${styles.heatIcon}`} />}
      <span>{TYPE_LABEL[m.type] || '-'}</span>
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.pageTitle}>
          <div className={styles.titleIcon}><IconMeter /></div>
          <h1>Mittarien hallinta</h1>
        </div>

        {view === 'list' && (
          <>
            {/* Suodatinpillerit + Uusi mittari */}
            <div className={styles.tabsRow}>
              {[
                { key: 'water', label: 'Vesi' },
                { key: 'electricity', label: 'Sähkö' },
                { key: 'heat', label: 'Lämpö' },
                { key: 'all', label: 'Kaikki' },
              ].map(f => (
                <button key={f.key} className={`${styles.filterPill} ${filter === f.key ? styles.filterPillActive : ''}`} onClick={() => setFilter(f.key)}>
                  {f.label}
                </button>
              ))}
              <div className={styles.mlAuto}><button className={styles.primaryBtn} onClick={openCreate}>+ Uusi mittari</button></div>
            </div>

            {/* Taulukko */}
            <div className={styles.userlistCard}>
              <table className={styles.userTable}>
                <thead className={styles.tableHead}>
                  <tr>
                    <th className={styles.colName}>Mittarin nimi</th>
                    <th className={styles.colType}>Tyyppi</th>
                    <th className={styles.colUnit}>Yksikkö</th>
                    <th className={styles.colMethod}>Mittaustapa</th>
                    <th className={styles.colState}>Tila</th>
                    <th className={styles.colLast}>Viimeisin lukema</th>
                    <th className={styles.colActions}>Toiminnot</th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {filtered.length===0 && (<tr><td colSpan={7} className={styles.muted}>Ei mittareita.</td></tr>)}
                  {filtered.map((m)=>(
                    <tr key={m.id} className={styles.memberRow} onClick={()=>handleRowClick(m)}>
                      <td>{m.name}</td>
                      <td>{renderTypeCell(m)}</td>
                      <td>{UNIT_LABEL[m.unit] || '-'}</td>
                      <td>{METHOD_LABEL[m.method] || '-'}</td>
                      <td>{m.active ? <button className={styles.statusActiveBtn}>Aktiivinen</button> : <button className={styles.statusInactiveBtn}>Pois käytöstä</button>}</td>
                      <td>{fmtDate(m.lastReadingAt)}</td>
                      <td>
                        <div className={styles.actions}>
                          <button className={styles.stateButton} onClick={(e)=>handleEdit(e,m)}>Muokkaa</button>
                          <button className={styles.stateButton} onClick={(e)=>handleOpenReadings(e,m)}>Lukemat</button>
                          <button className={styles.deleteBtn} onClick={(e)=>handleDelete(e,m)}>Poista</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ============= INTEGRAATIOMITTARIN LUKEMAT (VESI) ============= */}
        {view === 'readings' && readingMeter && (
          <>
            <div className={styles.backRow}><button className={styles.backLink} onClick={()=>setView('list')}>&lt; Takaisin mittareihin</button></div>
            <div className={`${styles.userlistCard} ${styles.readingCard}`}>
              <div className={styles.readingHeader}>
                <div className={styles.readingTitle}>
                  <span className={styles.readingName}>{readingMeter.name}</span>
                  <span className={styles.readingSub}>kulutuslukemat</span>
                </div>
                <div className={styles.aggPills}>
                  {['day','week','month'].map(k=>(
                    <button key={k} className={`${styles.filterPill} ${agg===k?styles.filterPillActive:''}`} onClick={()=>{ setAgg(k); setCursor(0); }}>
                      {k==='day'?'Päivä':k==='week'?'Viikko':'Kuukausi'}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.chartNav}>
                <IconButton direction="left" onClick={()=>setCursor(c=>c-1)} />
                <div className={styles.chartNavLabel}>{periodLabel}</div>
                <IconButton direction="right" onClick={()=>setCursor(c=>c+1)} />
              </div>

              <div className={styles.chartContainer}>
                <BarChartStacked data={readingsData} unit={readingMeter.unit || 'm3'} height={340} />
              </div>

              {/* KPI-kortit */}
              <div className={styles.summaryGrid}>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryTitle}>{windowSummary.title} – kulutus</div>
                  <div className={styles.summaryRow}>
                    <WaterIcon className={`${styles.dropIcon} ${styles.dropBlue}`} />
                    <div className={styles.summaryValue}><span>Kylmä</span><strong>{windowSummary.cold.toFixed(1)} m³</strong></div>
                  </div>
                  <div className={styles.summaryRow}>
                    <WaterIcon className={`${styles.dropIcon} ${styles.dropOrange}`} />
                    <div className={styles.summaryValue}><span>Lämmin</span><strong>{windowSummary.hot.toFixed(1)} m³</strong></div>
                  </div>
                  <div className={styles.summaryRowTotal}><span>Yhteensä</span><strong>{windowSummary.total.toFixed(1)} m³</strong></div>
                </div>

                <div className={styles.summaryCard}>
                  <div className={styles.summaryTitle}>Talonyhtiön kokonaiskulutus (sama ikkuna)</div>
                  <div className={styles.summaryRow}>
                    <WaterIcon className={`${styles.dropIcon} ${styles.dropBlue}`} />
                    <div className={styles.summaryValue}><span>Kylmä</span><strong>{makeBuildingStats(readingMeter.id, windowSummary).building.cold.toFixed(1)} m³ <span className={styles.mutedSmall}>({makeBuildingStats(readingMeter.id, windowSummary).shares.cold.toFixed(1)}%)</span></strong></div>
                  </div>
                  <div className={styles.summaryRow}>
                    <WaterIcon className={`${styles.dropIcon} ${styles.dropOrange}`} />
                    <div className={styles.summaryValue}><span>Lämmin</span><strong>{makeBuildingStats(readingMeter.id, windowSummary).building.hot.toFixed(1)} m³ <span className={styles.mutedSmall}>({makeBuildingStats(readingMeter.id, windowSummary).shares.hot.toFixed(1)}%)</span></strong></div>
                  </div>
                  <div className={styles.summaryRowTotal}>
                    <span>Yhteensä</span>
                    <strong>{makeBuildingStats(readingMeter.id, windowSummary).building.total.toFixed(1)} m³ <span className={styles.mutedSmall}>({makeBuildingStats(readingMeter.id, windowSummary).shares.total.toFixed(1)}%)</span></strong>
                  </div>
                </div>

                <div className={styles.summaryCard}>
                  <div className={styles.summaryTitle}>Mittarin sijoitus</div>
                  {(() => {
                    const bs = makeBuildingStats(readingMeter.id, windowSummary);
                    return (
                      <>
                        <div className={styles.summaryRow}>
                          <WaterIcon className={`${styles.dropIcon} ${styles.dropBlue}`} />
                          <div className={styles.summaryValue}><span>Kylmä</span><strong>{bs.ranks.cold}/{bs.rankTotal}</strong></div>
                        </div>
                        <div className={styles.summaryRow}>
                          <WaterIcon className={`${styles.dropIcon} ${styles.dropOrange}`} />
                          <div className={styles.summaryValue}><span>Lämmin</span><strong>{bs.ranks.hot}/{bs.rankTotal}</strong></div>
                        </div>
                        <div className={styles.summaryRowTotal}><span>Yhteensä</span><strong>{bs.ranks.total}/{bs.rankTotal}</strong></div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ================= MANUAALINEN (SÄHKÖ/KWH) ================= */}
        {view === 'manual' && readingMeter && (
          <>
            <div className={styles.backRow}>
              <button className={styles.backLink} onClick={()=>setView('list')}>&lt; Takaisin mittareihin</button>
            </div>

            <div className={`${styles.userlistCard} ${styles.readingCard}`}>
              <div className={styles.readingHeader}>
                <div className={styles.readingTitle}>
                  <span className={styles.readingName}>{readingMeter.name}</span>
                  <span className={styles.readingSub}>manuaaliset lukemat</span>
                </div>

                {/* Ajanjaksovalinta */}
                <div className={styles.aggPills}>
                  {[
                    { key: '30d', label: '30 pv' },
                    { key: '90d', label: '90 pv' },
                    { key: '12m', label: '12 kk' },
                  ].map(p => (
                    <button
                      key={p.key}
                      className={`${styles.filterPill} ${manPeriod === p.key ? styles.filterPillActive : ''}`}
                      onClick={() => setManPeriod(p.key)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>

                {/* +Uusi lukema */}
                <button className={`${styles.primaryBtn} ${styles.addReadingBtn}`} onClick={()=>setAddOpen(true)}>+ Uusi lukema</button>
              </div>

              {/* Yhteenveto-kortit */}
              <div className={styles.summaryGridManual}>
                <div className={styles.summaryCard}>
                  <div className={styles.summaryTitle}>Kulutus yhteensä (jakso)</div>
                  <div className={styles.bigNumber}>
                    {manSummary.canCalc ? `${manSummary.total.toFixed(1)} ${readingMeter.unit || 'kWh'}` : <span className={styles.muted}>Ei tarpeeksi lukemia</span>}
                  </div>
                  {manWindow && (
                    <div className={styles.mutedSmall}>
                      {fmtDate(manWindow.start)} – {fmtDate(manWindow.end)} · {manSummary.daysCount} pv
                    </div>
                  )}
                </div>

                <div className={styles.summaryCard}>
                  <div className={styles.summaryTitle}>Keskimäärin / vrk</div>
                  <div className={styles.bigNumber}>
                    {manSummary.canCalc ? `${manSummary.avgPerDay} ${readingMeter.unit || 'kWh'}` : <span className={styles.muted}>–</span>}
                  </div>
                  <div className={styles.mutedSmall}>Normalisoitu jaksopäivillä</div>
                </div>

                <div className={styles.summaryCard}>
                  <div className={styles.summaryTitle}>Suurin kasvu</div>
                  <div className={styles.bigNumber}>
                    {manSummary.canCalc && manSummary.maxDelta
                      ? `${manSummary.maxDelta.value.toFixed(1)} ${readingMeter.unit || 'kWh'}`
                      : <span className={styles.muted}>–</span>}
                  </div>
                  <div className={styles.mutedSmall}>
                    {manSummary.maxDelta ? `${fmtDate(manSummary.maxDelta.from)} → ${fmtDate(manSummary.maxDelta.to)}` : '—'}
                  </div>
                </div>

                <div className={styles.summaryCard}>
                  <div className={styles.summaryTitle}>Trendi vs. edellinen jakso</div>
                  <div className={styles.bigNumber}>
                    {manSummary.canCalc && manSummary.trendPct != null
                      ? `${manSummary.trendPct > 0 ? '↑' : manSummary.trendPct < 0 ? '↓' : '→'} ${Math.abs(manSummary.trendPct)} %`
                      : <span className={styles.muted}>–</span>}
                  </div>
                  <div className={styles.mutedSmall}>Sama pituus, suoraan ennen</div>
                </div>
              </div>

              {/* Pikasuodattimet taulukolle */}
              <div className={styles.tableFiltersRow}>
                <span className={styles.mutedSmall}>Näytä:</span>
                <button className={`${styles.filterPillTiny} ${onlyResets ? styles.filterPillActive : ''}`} onClick={()=>setOnlyResets(v=>!v)}>Nollaukset</button>
                <button className={`${styles.filterPillTiny} ${onlyAnomalies ? styles.filterPillActive : ''}`} onClick={()=>setOnlyAnomalies(v=>!v)}>Poikkeavat deltat</button>
                <button className={`${styles.filterPillTiny} ${onlyDuplicates ? styles.filterPillActive : ''}`} onClick={()=>setOnlyDuplicates(v=>!v)}>Duplikaattipäivät</button>
              </div>

              {/* Historia-taulukko */}
              <div className={styles.manualHistory}>
                <table className={styles.userTable}>
                  <thead className={styles.tableHead}>
                    <tr>
                      <th style={{width:'18%'}}>Päivä</th>
                      <th style={{width:'20%'}}>Lukema ({readingMeter.unit || 'kWh'})</th>
                      <th style={{width:'20%'}}>Muutos edelliseen</th>
                      <th style={{width:'18%'}}>Päivien väli</th>
                      <th>Huomio</th>
                    </tr>
                  </thead>
                  <tbody className={styles.tableBody}>
                    {manualFilteredDesc.length===0 && (<tr><td colSpan={5} className={styles.muted}>Ei lukemia valituilla suodattimilla.</td></tr>)}
                    {manualFilteredDesc.map((r)=>(
                      <tr key={r.id} className={styles.memberRow}>
                        <td>
                          {fmtDate(r.date)}{manualDuplicates.has(r.date) && <span className={styles.badgeWarn}>dupl.</span>}
                          {r.reset && <span className={styles.badgeInfo}>nollaus</span>}
                        </td>
                        <td>{Number.isFinite(r.value) ? r.value.toFixed(0) : r.value}</td>
                        <td>{r.delta==null ? <span className={styles.mutedSmall}>—</span> : `${r.delta.toFixed(1)} ${readingMeter.unit || 'kWh'}`}</td>
                        <td>{r.days==null ? <span className={styles.mutedSmall}>—</span> : `${r.days} pv`}</td>
                        <td>{r.note || <span className={styles.mutedSmall}>—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* UUSI LUKEMA - DIALOGI */}
            {addOpen && (
              <>
                <div className={styles.modalBackdrop} onClick={()=>setAddOpen(false)} aria-hidden="true" />
                <div className={styles.modalRoot} role="dialog" aria-modal="true" aria-labelledby="add-reading-title" onClick={()=>setAddOpen(false)}>
                  <div className={styles.modalCard} onClick={(e)=>e.stopPropagation()}>
                    <button className={styles.modalClose} aria-label="Sulje" onClick={()=>setAddOpen(false)}>×</button>
                    <h3 id="add-reading-title" className={styles.modalTitleLarge}>Uusi lukema</h3>

                    <div className={styles.formRow}>
                      <label className={styles.label}>Päivämäärä</label>
                      <input ref={addDateRef} className={styles.input} type="date" value={addDate} onChange={(e)=>setAddDate(e.target.value)} />
                    </div>
                    <div className={styles.formRow}>
                      <label className={styles.label}>Lukema ({readingMeter.unit || 'kWh'})</label>
                      <input className={styles.input} type="number" inputMode="decimal" placeholder="Esim. 12560"
                             value={addValue} onChange={(e)=>setAddValue(e.target.value)} />
                    </div>
                    <div className={styles.formRow}>
                      <label className={styles.label}>Huomio (valinnainen)</label>
                      <input className={styles.input} placeholder="Lyhyt muistiinpano"
                             value={addNote} onChange={(e)=>setAddNote(e.target.value)} />
                    </div>
                    <label className={styles.resetFlag}>
                      <input type="checkbox" checked={addReset} onChange={(e)=>setAddReset(e.target.checked)} />
                      <span>Mittari nollautui / vaihdettiin</span>
                    </label>

                    <div className={styles.modalFooterLeftAligned}>
                      <button className={styles.saveBtn} onClick={saveNewReading}>Tallenna lukema</button>
                      <button className={styles.cancelBtnTight} onClick={()=>setAddOpen(false)}>Peruuta</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Modaali: mittarin luonti/muokkaus */}
        {modalOpen && (
          <>
            <div className={styles.modalBackdrop} onClick={closeModal} aria-hidden="true" />
            <div className={styles.modalRoot} role="dialog" aria-modal="true" aria-labelledby="meter-modal-title" onClick={closeModal}>
              <div className={styles.modalCard} onClick={(e)=>e.stopPropagation()}>
                <button className={styles.modalClose} aria-label="Sulje" onClick={closeModal}>×</button>
                <h3 id="meter-modal-title" className={styles.modalTitleLarge}>{mode==='create'?'Uusi mittari':'Muokkaa mittaria'}</h3>

                <div className={styles.formRow}>
                  <label className={styles.label}>Mittarin nimi</label>
                  <input ref={nameRef} className={`${styles.input} ${showErr('name')?styles.inputError:''}`} placeholder="Esim. Sähkö pääkeskus"
                         value={form.name} onChange={(e)=>onChangeField('name', e.target.value)} onBlur={()=>onBlurField('name')} aria-invalid={!!errors.name}/>
                  {showErr('name') && <div className={styles.fieldError}>{errors.name}</div>}
                </div>

                <div className={styles.formRowGrid}>
                  <div className={styles.formCol}>
                    <label className={styles.label}>Tyyppi</label>
                    <select className={`${styles.input} ${showErr('type')?styles.inputError:''}`} value={form.type}
                            onChange={(e)=>onChangeField('type', e.target.value)} onBlur={()=>onBlurField('type')} aria-invalid={!!errors.type}>
                      <option value="">Valitse…</option>
                      <option value="water">Vesi</option>
                      <option value="electricity">Sähkö</option>
                      <option value="heat">Lämpö</option>
                    </select>
                    {showErr('type') && <div className={styles.fieldError}>{errors.type}</div>}
                  </div>
                  <div className={styles.formCol}>
                    <label className={styles.label}>Yksikkö</label>
                    <select className={`${styles.input} ${showErr('unit')?styles.inputError:''}`} value={form.unit}
                            onChange={(e)=>onChangeField('unit', e.target.value)} onBlur={()=>onBlurField('unit')} aria-invalid={!!errors.unit}>
                      <option value="">Valitse…</option>
                      <option value="m3">m3</option>
                      <option value="kwh">kWh</option>
                    </select>
                    {showErr('unit') && <div className={styles.fieldError}>{errors.unit}</div>}
                  </div>
                </div>

                <div className={styles.formRowGrid}>
                  <div className={styles.formCol}>
                    <label className={styles.label}>Mittaustapa</label>
                    <select className={`${styles.input} ${showErr('method')?styles.inputError:''}`} value={form.method}
                            onChange={(e)=>onChangeField('method', e.target.value)} onBlur={()=>onBlurField('method')} aria-invalid={!!errors.method}>
                      <option value="">Valitse…</option>
                      <option value="manual">Manuaalinen</option>
                      <option value="integration">Integraatio</option>
                    </select>
                    {showErr('method') && <div className={styles.fieldError}>{errors.method}</div>}
                  </div>
                  <div className={styles.formCol}>
                    <label className={styles.label}>Tila</label>
                    <div className={styles.roleGrid}>
                      <label className={`${styles.roleOption} ${form.active?styles.roleOptionSelected:''}`}>
                        <input type="checkbox" className={styles.roleCheckbox} checked={form.active} onChange={(e)=>onChangeField('active', e.target.checked)} />
                        <span className={styles.roleText}>{form.active?'Aktiivinen':'Pois käytöstä'}</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className={styles.formRow}>
                  <label className={styles.label}>Viimeisin lukema (pvm)</label>
                  <input className={styles.input} type="date" value={form.lastReadingAt || ''} onChange={(e)=>onChangeField('lastReadingAt', e.target.value)} />
                </div>

                <div className={styles.modalFooterLeftAligned}>
                  <button className={styles.primaryBtn} disabled={busy} onClick={onSubmit}>{mode==='create'?'Luo mittari':'Tallenna muutokset'}</button>
                  <button className={styles.cancelBtnTight} onClick={closeModal}>Peruuta</button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

